import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ArrowLeft, X, ChevronLeft, ChevronRight, Upload, Plus, Trash2, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const resolveUrl = (url) => {
  if (!url) return "";
  if (url.includes("/api/files/") && !url.startsWith(process.env.REACT_APP_BACKEND_URL)) {
    const filePath = url.substring(url.indexOf("/api/files/"));
    return `${process.env.REACT_APP_BACKEND_URL}${filePath}`;
  }
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

const categories = [
  { value: "all", label: "All Styles" },
  { value: "braids", label: "Braids" },
  { value: "twists", label: "Twists" },
  { value: "cornrows", label: "Cornrows" },
  { value: "crochet", label: "Crochet" },
  { value: "locs", label: "Faux Locs" },
  { value: "mens", label: "Men's" },
];

const PortfolioPage = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [images, setImages] = useState([]);
  const [services, setServices] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({ alt: "", category: "braids" });
  const portfolioFileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(() => setIsAdmin(true))
      .catch(() => setIsAdmin(false));
  }, []);

  const fetchData = async () => {
    try {
      const [galRes, svcRes] = await Promise.all([
        axios.get(`${API}/gallery`),
        axios.get(`${API}/services`),
      ]);
      setImages(galRes.data);
      setServices(svcRes.data);
    } catch (err) {
      console.error("Failed to load portfolio data:", err);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const uploadRes = await axios.post(`${API}/admin/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = uploadRes.data.url;
      const mediaType = file.type.startsWith("video/") ? "video" : "image";
      await axios.post(`${API}/admin/gallery`, {
        src: fullUrl,
        alt: uploadForm.alt || file.name.split(".")[0],
        category: uploadForm.category,
        media_type: mediaType,
      }, { withCredentials: true });
      toast.success("Photo added to portfolio!");
      setUploadForm({ alt: "", category: "braids" });
      setShowUpload(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this photo from portfolio?")) return;
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, { withCredentials: true });
      toast.success("Photo removed");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const filtered =
    activeTab === "all"
      ? images
      : images.filter((img) => img.category === activeTab);

  const openLightbox = (idx) => {
    setLightboxIdx(idx);
    setLightboxOpen(true);
  };

  const nextImage = () => setLightboxIdx((prev) => (prev + 1) % filtered.length);
  const prevImage = () => setLightboxIdx((prev) => (prev - 1 + filtered.length) % filtered.length);

  const scrollToBooking = () => {
    window.location.href = "/#booking";
  };

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="portfolio-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-brand-bg/90 backdrop-blur-xl border-b border-brand-border/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="font-serif text-xl md:text-2xl font-semibold text-brand-text" data-testid="portfolio-home-link">
            Afro <span className="text-brand-gold">Magic</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-1.5 font-sans text-sm text-brand-text-muted hover:text-brand-gold transition-colors" data-testid="portfolio-back-link">
              <ArrowLeft size={16} /> Back to Home
            </Link>
            <button onClick={scrollToBooking} data-testid="portfolio-book-btn" className="hidden md:inline-block bg-brand-gold text-black font-sans font-semibold text-sm px-6 py-2 hover:bg-brand-gold-light transition-colors">
              Book Now
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <p className="text-xs uppercase tracking-[0.3em] font-sans text-brand-gold mb-4">Our Portfolio</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-text tracking-tight leading-tight mb-4">
              The Art of <span className="text-gold-gradient">Braiding</span>
            </h1>
            <p className="font-sans text-base text-brand-text-muted max-w-lg mx-auto leading-relaxed">
              Every braid we create is a unique work of art. Browse our portfolio to find your next style inspiration.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Bar */}
      <div className="border-y border-brand-border py-6 mb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-wrap items-center justify-center gap-8 md:gap-16">
          <div className="text-center">
            <p className="font-serif text-2xl text-brand-gold font-semibold">{images.length}+</p>
            <p className="font-sans text-xs text-brand-text-muted uppercase tracking-wider">Styles Created</p>
          </div>
          <div className="text-center">
            <p className="font-serif text-2xl text-brand-gold font-semibold">{services.length}</p>
            <p className="font-sans text-xs text-brand-text-muted uppercase tracking-wider">Service Types</p>
          </div>
          <div className="text-center">
            <p className="font-serif text-2xl text-brand-gold font-semibold">5.0</p>
            <p className="font-sans text-xs text-brand-text-muted uppercase tracking-wider">Star Rating</p>
          </div>
          <div className="text-center">
            <p className="font-serif text-2xl text-brand-gold font-semibold">2K+</p>
            <p className="font-sans text-xs text-brand-text-muted uppercase tracking-wider">Happy Clients</p>
          </div>
        </div>
      </div>

      {/* Admin Upload Panel */}
      {isAdmin && (
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-8">
          <div className="border border-brand-gold/30 bg-brand-surface p-5" data-testid="portfolio-upload-panel">
            <p className="font-sans text-sm text-brand-gold font-semibold mb-3 uppercase tracking-wider">Upload Photos & Videos</p>
            <p className="font-sans text-xs text-brand-text-muted mb-3">Pick a category, then choose your file — it uploads and appears instantly.</p>
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
              <div className="w-full sm:w-48">
                <label className="font-sans text-xs text-brand-text-muted mb-1 block">Category</label>
                <Select value={uploadForm.category} onValueChange={(val) => setUploadForm({ ...uploadForm, category: val })}>
                  <SelectTrigger className="bg-brand-bg border-brand-border text-brand-text rounded-none" data-testid="portfolio-upload-category">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="braids">Braids</SelectItem>
                    <SelectItem value="twists">Twists</SelectItem>
                    <SelectItem value="cornrows">Cornrows</SelectItem>
                    <SelectItem value="crochet">Crochet</SelectItem>
                    <SelectItem value="locs">Faux Locs</SelectItem>
                    <SelectItem value="mens">Men's</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 w-full">
                <label className="font-sans text-xs text-brand-text-muted mb-1 block">Choose file</label>
                <input
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  data-testid="portfolio-file-input"
                  className="block w-full text-sm text-brand-text-muted file:mr-4 file:py-2 file:px-4 file:border file:border-brand-gold/40 file:text-sm file:font-sans file:font-semibold file:bg-brand-surface file:text-brand-gold hover:file:bg-brand-gold hover:file:text-black file:cursor-pointer file:transition-colors file:rounded-none cursor-pointer"
                />
              </div>
            </div>
            {uploading && <p className="font-sans text-xs text-brand-gold mt-2 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Uploading and saving...</p>}
          </div>
        </div>
      )}

      {/* Portfolio Grid */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex justify-center mb-10">
              <TabsList className="bg-brand-surface border border-brand-border h-auto p-1 gap-1 flex-wrap" data-testid="portfolio-tabs">
                {categories.map((cat) => (
                  <TabsTrigger
                    key={cat.value}
                    value={cat.value}
                    data-testid={`portfolio-tab-${cat.value}`}
                    className="font-sans text-sm px-4 py-2 data-[state=active]:bg-brand-gold data-[state=active]:text-black data-[state=active]:shadow-none text-brand-text-muted rounded-none"
                  >
                    {cat.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {categories.map((cat) => {
              const items = cat.value === "all" ? images : images.filter((img) => img.category === cat.value);
              const svcById = Object.fromEntries(services.map((s) => [s.id, s]));
              const goToBooking = (img) => {
                window.location.href = img.service_id ? `/?service=${img.service_id}#booking` : "/#booking";
              };
              return (
                <TabsContent key={cat.value} value={cat.value}>
                  {items.length === 0 ? (
                    <p className="text-center font-sans text-sm text-brand-text-muted py-16">No images in this category yet. {isAdmin && "Use the upload panel above to add photos."}</p>
                  ) : (
                    <>
                      <p className="text-center font-sans text-xs text-brand-text-muted mb-6">{items.length} {items.length === 1 ? "item" : "items"}</p>
                      <div className="columns-1 md:columns-2 gap-6 space-y-6 max-w-5xl mx-auto">
                        {items.map((img, i) => (
                          <motion.div
                            key={`${cat.value}-${img.id || i}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.8) }}
                            className="break-inside-avoid group cursor-pointer relative overflow-hidden border border-brand-border hover:border-brand-gold transition-colors"
                            onClick={() => goToBooking(img)}
                            data-testid={`portfolio-image-${i}`}
                          >
                            {img.media_type === "video" ? (
                              <div className="relative">
                                <video
                                  src={resolveUrl(img.src)}
                                  className="w-full transition-transform duration-500 group-hover:scale-105"
                                  controls
                                  playsInline
                                  preload="metadata"
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <div className="absolute top-2 right-2 bg-black/70 px-2 py-0.5 rounded text-[10px] font-sans text-brand-gold pointer-events-none">VIDEO</div>
                              </div>
                            ) : (
                              <img
                                src={resolveUrl(img.src)}
                                alt={img.alt}
                                loading="lazy"
                                className="w-full transition-transform duration-500 group-hover:scale-105"
                              />
                            )}
                            <div className="bg-brand-surface border-t border-brand-border p-4 flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className="font-sans text-base text-brand-text font-semibold truncate">
                                  {svcById[img.service_id]?.name || img.alt.replace(" - Afro Magic Braiding Hair", "")}
                                </p>
                                {svcById[img.service_id]?.starting_price != null && (
                                  <p className="font-sans text-sm text-brand-gold font-semibold">${svcById[img.service_id].starting_price}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="font-sans text-xs uppercase tracking-wider bg-brand-gold text-black px-3 py-1.5 font-semibold whitespace-nowrap">Click Now to Book</span>
                                {isAdmin && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleDelete(img.id); }}
                                    data-testid={`portfolio-delete-${img.id}`}
                                    className="p-1.5 bg-red-500/20 border border-red-400 text-red-400 hover:bg-red-500/40 transition-colors"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </>
                  )}
                </TabsContent>
              );
            })}
          </Tabs>
        </div>
      </section>

      {/* Services Showcase */}
      <section className="py-16 bg-brand-surface border-t border-brand-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-2xl md:text-3xl text-brand-text text-center mb-10">Our Specialties</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {services.map((svc) => (
              <div
                key={svc.id}
                className="text-center p-4 border border-brand-border bg-brand-bg hover:border-brand-gold/30 transition-colors"
                data-testid={`portfolio-service-${svc.id}`}
              >
                <p className="font-sans text-sm text-brand-text font-medium mb-1">{svc.name}</p>
                <p className="font-sans text-xs text-brand-gold">${svc.starting_price}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <button
              onClick={scrollToBooking}
              data-testid="portfolio-cta-btn"
              className="bg-brand-gold text-black font-sans font-semibold text-sm px-10 py-4 hover:bg-brand-gold-light transition-colors"
            >
              Book Your Style Today
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-brand-border py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="font-serif text-lg text-brand-text">
            Afro <span className="text-brand-gold">Magic</span>
          </Link>
          <p className="font-sans text-xs text-brand-text-muted">booking@afromagicbraiding.shop | (313) 484-7307</p>
          <p className="font-sans text-xs text-brand-text-muted/60">&copy; {new Date().getFullYear()} Afro Magic Braiding Hair</p>
        </div>
      </footer>

      {/* Lightbox Dialog */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-4xl bg-brand-bg border-brand-border p-0 overflow-hidden" data-testid="portfolio-lightbox">
          <DialogTitle className="sr-only">Portfolio Image</DialogTitle>
          {filtered[lightboxIdx] && (
            <div className="relative">
              {filtered[lightboxIdx].media_type === "video" ? (
                <video
                  src={resolveUrl(filtered[lightboxIdx].src)}
                  className="w-full max-h-[80vh] object-contain"
                  controls
                  autoPlay
                  playsInline
                />
              ) : (
                <img
                  src={resolveUrl(filtered[lightboxIdx].src)}
                  alt={filtered[lightboxIdx].alt}
                  className="w-full max-h-[80vh] object-contain"
                />
              )}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="font-sans text-sm text-brand-text font-medium">{filtered[lightboxIdx].alt}</p>
                <p className="font-sans text-xs text-brand-gold capitalize">{filtered[lightboxIdx].category}</p>
              </div>
              {filtered.length > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    data-testid="lightbox-prev"
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 border border-brand-border flex items-center justify-center text-brand-text hover:text-brand-gold transition-colors"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    data-testid="lightbox-next"
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 border border-brand-border flex items-center justify-center text-brand-text hover:text-brand-gold transition-colors"
                  >
                    <ChevronRight size={20} />
                  </button>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortfolioPage;
